import React, { useCallback, useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Context
import { boardTemplatePickerContext } from '../context/boardTemplatePickerContext';

// Utils
import { getElementId } from '../../../../common/elements/utils/elementPropertyUtils';
import { getElementTemplateLink } from '../../../../common/templates/templateUtil';
import { getTemplateIsCustom } from '../boardTemplatePickerUtil';
import { sendAmplitudeEvent } from '../../../analytics/amplitudeService';

// Actions
import { copyTemplateBoardContents } from '../boardTemplateActions';
import { openPopup } from '../../../components/popupPanel/popupActions';

// Selectors
import { selectedTemplateBoardSelector } from '../boardTemplatePickerSelectors';
import { contentTotalCountSelector, contentTotalLimitSelector } from '../../../user/elementCount/elementCountSelector';

// Components
import Button from '../../../components/buttons/Button';
import ClearTemplateToggle from './ClearTemplateToggle';

// Constants
import { AMPLITUDE_USER_PROPS, TRACKED_FEATURES } from '../../../../common/analytics/statsConstants';
import { ELEMENT_COUNT_LIMIT_POPUP } from '../../../user/elementCount/elementCountConstants';
import { ImMNElement } from '../../../../common/elements/elementModelTypes';

import './BoardTemplatePickerFooter.scss';

type Props = {
    currentBoard: ImMNElement;
    dismissTemplatePicker: () => void;
};

const BoardTemplatePickerFooter = ({ currentBoard, dismissTemplatePicker }: Props) => {
    const {
        templates,
        selectTemplate,

        setIsCopyingTemplate,

        selectedTemplateId,

        keepTemplateContentChecked,
        setKeepTemplateContentChecked,
    } = useContext(boardTemplatePickerContext);

    const selectedTemplateBoard = useSelector((state) => selectedTemplateBoardSelector(state, selectedTemplateId));
    const contentCountTotal = useSelector(contentTotalCountSelector);
    const contentCountLimit = useSelector(contentTotalLimitSelector);

    const templateForceClear = selectedTemplateId ? templates[selectedTemplateId]?.forceClear : false;
    const toggleKeepTemplateDisabled = !selectedTemplateId || templateForceClear;

    // if checkbox for switching keep/clear state of template becomes disabled,
    // uncheck the checkbox
    useEffect(() => {
        if (keepTemplateContentChecked && toggleKeepTemplateDisabled) {
            setKeepTemplateContentChecked(false);
        }
    }, [keepTemplateContentChecked, toggleKeepTemplateDisabled]);

    const dispatch = useDispatch();

    const dispatchCopyTemplateContents = (copyTemplateBoardId: string, currentBoard: ImMNElement) =>
        dispatch(copyTemplateBoardContents(copyTemplateBoardId, currentBoard));

    const dispatchOpenBillingAlertPopup = () => dispatch(openPopup(ELEMENT_COUNT_LIMIT_POPUP, () => true));

    const copyTemplate = useCallback(async () => {
        dismissTemplatePicker();

        if (!selectedTemplateId) return;

        // If the user is over their content limit,
        // dismiss the template and navigate to the account upgrade page
        if (contentCountTotal >= contentCountLimit) {
            selectTemplate(null);
            return dispatchOpenBillingAlertPopup();
        }

        setIsCopyingTemplate(true);

        const copyTemplateId =
            (keepTemplateContentChecked
                ? getElementId(selectedTemplateBoard)
                : getElementTemplateLink(selectedTemplateBoard)) || selectedTemplateId;

        await dispatchCopyTemplateContents(copyTemplateId, currentBoard);

        selectTemplate(null);
        setIsCopyingTemplate(false);

        if (selectedTemplateId) {
            const isCustomTemplate = getTemplateIsCustom(templates[selectedTemplateId]);

            sendAmplitudeEvent({
                eventType: 'Used template',
                eventProperties: {
                    templateId: isCustomTemplate ? 'custom' : selectedTemplateId,
                },
                userProperties: {
                    [AMPLITUDE_USER_PROPS.FEATURE]: { [TRACKED_FEATURES.TEMPLATE_PICKER]: true },
                },
            });
        }
    }, [selectedTemplateId, selectedTemplateBoard, currentBoard, keepTemplateContentChecked]);

    return (
        <div className="BoardTemplatePickerFooter">
            <Button className="StyledButton primary use-template-button" onClickFn={copyTemplate}>
                {selectedTemplateId ? 'Use this template' : 'Continue'}
            </Button>

            <ClearTemplateToggle
                checked={keepTemplateContentChecked}
                setChecked={setKeepTemplateContentChecked}
                disabled={toggleKeepTemplateDisabled}
            />
        </div>
    );
};

export default BoardTemplatePickerFooter;
